<template>
<div class="kt-portfolio-forecast">
	<b-tabs content-class="pfo-tabs-content" v-model="d_activeTab">
		<b-tab title="Gantt View" active>
			<div class="pfo-gantt-wrap">
				<kt-revenue-forecast-gantt
					:projects="c_viewProjects"
					:from-date="c_ganttFromDate"
					:to-date="c_ganttToDate"
					:revenue-view="revenueView"
				/>
			</div>
		</b-tab>

		<b-tab title="Chart View">
			<div class="pfo-summary">
				<div class="pfo-metric">
					<span class="pfo-metric-lbl">{{ c_summary.totalLabel }}</span>
					<span class="pfo-metric-val">{{ fmt(c_summary.totalBacklog) }}</span>
				</div>
				<div class="pfo-metric">
					<span class="pfo-metric-lbl">Active Projects</span>
					<span class="pfo-metric-val">{{ c_summary.activeCount }}</span>
				</div>
				<div class="pfo-metric">
					<span class="pfo-metric-lbl">Projects In Pursuit</span>
					<span class="pfo-metric-val">{{ c_summary.inPursuitCount }}</span>
				</div>
				<div class="pfo-metric">
					<span class="pfo-metric-lbl">Peak Monthly</span>
					<span class="pfo-metric-val">{{ fmt(c_summary.peakMonthly) }}</span>
				</div>
				<div class="pfo-metric">
					<span class="pfo-metric-lbl">Avg Monthly</span>
					<span class="pfo-metric-val">{{ fmt(c_summary.avgMonthly) }}</span>
				</div>
			</div>

			<div class="pfo-chart-wrap">
				<canvas ref="stackedCanvas" />
			</div>
			<div class="pfo-chart-wrap pfo-chart-wrap-sm">
				<canvas ref="cumulativeCanvas" />
			</div>
		</b-tab>

		<b-tab title="Cash Flow">
			<div class="pfo-table-wrap pfo-table-wrap-wide">
				<b-table-simple small hover striped class="pfo-cashflow-table pfo-cashflow-table-transposed">
					<b-thead>
						<b-tr>
							<b-th class="pfo-sticky-col">Project</b-th>
							<b-th
								v-for="col in c_cashflowMonthCols"
								:key="col.monthKey"
								class="text-right"
							>{{ col.monthLabel }}</b-th>
							<b-th class="text-right">Total</b-th>
						</b-tr>
					</b-thead>
					<b-tbody>
						<b-tr v-for="row in c_cashflowProjectRows" :key="row.projectId">
							<b-td class="pfo-sticky-col" :style="{ borderLeftColor: c_projectColor(row.colorIx) }">{{ row.name }}</b-td>
							<b-td
								v-for="(cell, cix) in row.cells"
								:key="cix"
								class="text-right"
							>{{ cell }}</b-td>
							<b-td class="text-right font-weight-bold">{{ fmt(row.total) }}</b-td>
						</b-tr>
					</b-tbody>
					<b-tfoot>
						<b-tr class="pfo-footer-row">
							<b-td class="pfo-sticky-col">Monthly Total</b-td>
							<b-td
								v-for="(cell, cix) in c_cashflowMonthTotals"
								:key="cix"
								class="text-right"
							>{{ cell }}</b-td>
							<b-td class="text-right font-weight-bold">{{ fmt(c_summary.totalBacklog) }}</b-td>
						</b-tr>
					</b-tfoot>
				</b-table-simple>
			</div>
		</b-tab>
	</b-tabs>
</div>
</template>

<script>
import Chart from 'chart.js';
import KTRevenueForecastGantt from '@/components/Ctrl/KT-RevenueForecastGantt.vue';
import {
	PROJECT_COLORS,
	buildProjectRevenueMap,
	buildPortfolioTimeline,
	fn_buildDateRangeMonthKeys,
	fn_projectTotalValue,
	REVENUE_VIEW_CONTRACT,
	STAGE_AWARDED,
	fmt,
} from '@/utils/forecastUtils.js';

export default {
	name: 'kt-portfolio-forecast',
	components: {
		'kt-revenue-forecast-gantt': KTRevenueForecastGantt,
	},
	props: {
		projects: {
			type: Array,
			required: true,
		},
		displayProjects: {
			type: Array,
			default: null,
		},
		forecastFilter: {
			type: Object,
			default: null,
		},
		retainagePct: { type: Number, default: 0 },
		paymentLagDays: { type: Number, default: 0 },
		grossMarginPct: { type: Number, default: 0 },
		revenueView: {
			type: String,
			default: REVENUE_VIEW_CONTRACT,
		},
	},
	data() {
		return {
			TAB_PORTFOLIO: 1,
			d_activeTab: 0,
			d_stackedChart: null,
			d_cumulativeChart: null,
			fmt,
		};
	},
	computed: {
		c_viewProjects() {
			return this.displayProjects || this.projects;
		},
		c_ganttFromDate() {
			return this.forecastFilter && this.forecastFilter.fromDate
				? this.forecastFilter.fromDate
				: '';
		},
		c_ganttToDate() {
			return this.forecastFilter && this.forecastFilter.toDate
				? this.forecastFilter.toDate
				: '';
		},
		c_timeline() {
			if (this.forecastFilter && this.forecastFilter.fromDate && this.forecastFilter.toDate) {
				return fn_buildDateRangeMonthKeys(this.forecastFilter.fromDate, this.forecastFilter.toDate);
			}
			return buildPortfolioTimeline(this.c_viewProjects);
		},
		c_projectMaps() {
			//return this.c_viewProjects?.map(p => buildProjectRevenueMap(p, this.revenueView));
			const buildProjects=[...this.c_viewProjects?.map(p => buildProjectRevenueMap(p, this.revenueView))];
			console.log('buildProjects',buildProjects);
			return buildProjects;
		},
		c_combinedRevenue() {
			return this.c_timeline?.map(monthKey => {
				return this.c_projectMaps?.reduce((sum, map) => sum + (map[monthKey] || 0), 0);
			});
		},
		c_portfolioCumulative() {
			let running = 0;
			return this.c_combinedRevenue?.map(v => {
				running += v;
				return running;
			});
		},
		c_timelineLabels() {
			return this.c_timeline?.map(key => {
				const [y, m] = key.split('-').map(Number);
				const d = new Date(y, m - 1);
				return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
			});
		},
		c_summary() {
			const totalBacklog = this.c_viewProjects?.reduce(
				(s, p) => s + fn_projectTotalValue(p, this.revenueView), 0
			);
			const combined = this.c_combinedRevenue;
			const peakMonthly = combined.length ? Math.max(...combined) : 0;
			const avgMonthly = combined.length
				? combined.reduce((a, b) => a + b, 0) / combined.length
				: 0;
			return {
				totalBacklog,
				totalLabel: this.revenueView === REVENUE_VIEW_CONTRACT ? 'Total Contract' : 'Total Fee',
				activeCount: this.c_viewProjects?.filter(p => p.stage === STAGE_AWARDED).length,
				inPursuitCount: this.c_viewProjects?.filter(p => p.stage !== STAGE_AWARDED).length,
				peakMonthly,
				avgMonthly,
			};
		},
		c_cashflowMonthCols() {
			return this.c_timeline?.map((monthKey, i) => ({
				monthKey,
				monthLabel: this.c_timelineLabels[i],
			}));
		},
		c_cashflowProjectRows() {
			return this.c_viewProjects?.map((proj, ix) => {
				const map = this.c_projectMaps[ix];
				const cells = this.c_timeline?.map(monthKey => {
					const rev = map[monthKey];
					return rev != null ? fmt(rev) : '—';
				});
				const total = this.c_timeline?.reduce((s, monthKey) => s + (map[monthKey] || 0), 0);
				return {
					projectId: proj.id,
					name: proj.name,
					colorIx: ix,
					cells,
					total,
				};
			});
		},
		c_cashflowMonthTotals() {
			return this.c_timeline?.map(monthKey => {
				const total = this.c_projectMaps?.reduce((s, map) => s + (map[monthKey] || 0), 0);
				return total ? fmt(total) : '—';
			});
		},
	},
	watch: {
		projects: {
			deep: true,
			handler() {
				if (this.d_activeTab === this.TAB_PORTFOLIO) this.m_renderCharts();
			},
		},
		displayProjects: {
			deep: true,
			handler() {
				if (this.d_activeTab === this.TAB_PORTFOLIO) this.$nextTick(() => this.m_renderCharts());
			},
		},
		forecastFilter: {
			deep: true,
			handler() {
				if (this.d_activeTab === this.TAB_PORTFOLIO) this.$nextTick(() => this.m_renderCharts());
			},
		},
		revenueView() {
			if (this.d_activeTab === this.TAB_PORTFOLIO) this.$nextTick(() => this.m_renderCharts());
		},
		d_activeTab(val) {
			if (val === this.TAB_PORTFOLIO) this.$nextTick(() => this.m_renderCharts());
		},
	},
	mounted() {
		if (this.d_activeTab === this.TAB_PORTFOLIO) this.m_renderCharts();
	},
	beforeDestroy() {
		this.m_destroyCharts();
	},
	methods: {
		c_projectColor(ix) {
			return PROJECT_COLORS[ix % PROJECT_COLORS.length];
		},
		m_destroyCharts() {
			if (this.d_stackedChart) {
				this.d_stackedChart.destroy();
				this.d_stackedChart = null;
			}
			if (this.d_cumulativeChart) {
				this.d_cumulativeChart.destroy();
				this.d_cumulativeChart = null;
			}
		},
		m_renderCharts() {
			this.m_renderStackedChart();
			this.m_renderCumulativeChart();
		},
		m_renderStackedChart() {
			const canvas = this.$refs.stackedCanvas;
			if (!canvas) return;
			if (this.d_stackedChart) {
				this.d_stackedChart.destroy();
				this.d_stackedChart = null;
			}
			const labels = this.c_timelineLabels;
			const datasets = this.c_viewProjects.map((proj, ix) => ({
				label: proj.name,
				data: this.c_timeline.map(key => this.c_projectMaps[ix][key] || 0),
				backgroundColor: this.c_projectColor(ix),
				stack: 'portfolio',
			}));
			const ctx = canvas.getContext('2d');
			this.d_stackedChart = new Chart(ctx, {
				type: 'bar',
				data: { labels, datasets },
				options: {
					responsive: true,
					maintainAspectRatio: false,
					legend: { display: true, position: 'top' },
					tooltips: {
						callbacks: {
							label(tooltipItem, data) {
								const ds = data.datasets[tooltipItem.datasetIndex];
								return ds.label + ': ' + fmt(tooltipItem.yLabel);
							},
						},
					},
					scales: {
						xAxes: [{ stacked: true, gridLines: { display: false } }],
						yAxes: [{
							stacked: true,
							ticks: {
								beginAtZero: true,
								callback: v => fmt(v),
							},
						}],
					},
				},
			});
		},
		m_renderCumulativeChart() {
			const canvas = this.$refs.cumulativeCanvas;
			if (!canvas) return;
			if (this.d_cumulativeChart) {
				this.d_cumulativeChart.destroy();
				this.d_cumulativeChart = null;
			}
			const labels = this.c_timelineLabels;
			const ctx = canvas.getContext('2d');
			this.d_cumulativeChart = new Chart(ctx, {
				type: 'line',
				data: {
					labels,
					datasets: [{
						label: 'Portfolio Cumulative',
						data: this.c_portfolioCumulative,
						borderColor: '#0C447C',
						backgroundColor: 'rgba(12, 68, 124, 0.08)',
						fill: true,
						lineTension: 0.2,
						pointRadius: 2,
					}],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					legend: { display: true, position: 'top' },
					tooltips: {
						callbacks: {
							label(tooltipItem) {
								return 'Cumulative: ' + fmt(tooltipItem.yLabel);
							},
						},
					},
					scales: {
						xAxes: [{ gridLines: { display: false } }],
						yAxes: [{
							ticks: {
								beginAtZero: true,
								callback: v => fmt(v),
							},
						}],
					},
				},
			});
		},
	},
};
</script>

<style lang="less" scoped>
.kt-portfolio-forecast {
	font-size: 0.9em;
}

.pfo-tabs-content {
	padding-top: 0.75em;
}

.pfo-gantt-wrap {
	overflow-x: auto;
}

.pfo-summary {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5em 1.5em;
	margin-bottom: 0.75em;
	padding: 0.5em 0.75em;
	background: #f8f9fa;
	border-radius: 0.25em;
}

.pfo-metric {
	display: flex;
	flex-direction: column;
	.pfo-metric-lbl {
		font-size: 0.75em;
		color: #6c757d;
	}
	.pfo-metric-val {
		font-weight: 600;
		font-size: 0.95em;
	}
}

.pfo-chart-wrap {
	position: relative;
	height: 300px;
	margin-bottom: 0.75em;
	&.pfo-chart-wrap-sm {
		height: 220px;
	}
}

.pfo-table-wrap {
	max-height: 500px;
	overflow: auto;
}

.pfo-table-wrap-wide {
	max-width: 100%;
}

.pfo-cashflow-table {
	font-size: 0.85em;
	th {
		border-bottom: 2px solid #dee2e6;
		white-space: nowrap;
	}
}

.pfo-cashflow-table-transposed {
	.pfo-sticky-col {
		position: sticky;
		left: 0;
		z-index: 1;
		background: inherit;
		min-width: 140px;
		max-width: 180px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	tbody .pfo-sticky-col {
		border-left: 4px solid transparent;
		font-weight: 500;
	}

	thead .pfo-sticky-col,
	tfoot .pfo-sticky-col {
		background: #f8f9fa;
	}
}

.pfo-footer-row {
	font-weight: 600;
	background: #f8f9fa;
}
</style>
